import Button from 'ext-ui/components/Button/Button'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeeds/CardFeeds.style'
import mastercardLogoUrl from 'src/assets/images/issuerLogos/mastercard.svg'
import visaLogoUrl from 'src/assets/images/issuerLogos/visa.svg'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import SvgFilterFunnel from 'src/components/Svgs/SvgFilterFunnel'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  cardFeedDetailsUrl,
  createCardFeedUrl,
  homeUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  CardFeedResponse,
  CardFeedStatus,
  CardNetwork,
} from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeeds from 'src/queries/cardFeeds/domain/useQueryCardFeeds'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { Userpilot } from 'userpilot'

// Brand names — rendered as data, not localized.
const NETWORK_LABEL: Record<CardNetwork, string> = {
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  AMERICAN_EXPRESS: 'American Express',
}

const NETWORK_LOGO: Partial<Record<CardNetwork, string>> = {
  VISA: visaLogoUrl,
  MASTERCARD: mastercardLogoUrl,
}

// Status copy is localized; keys exist in en-US.json.
const STATUS_LABEL: Record<CardFeedStatus, string> = {
  ACTIVE: 'Connected',
  SUBMITTED: 'Pending',
  FAILED: 'Failed',
  DEACTIVATED: 'Disconnected',
}

// undefined → renders the Pill's default grey.
const STATUS_PILL_COLOR: Record<CardFeedStatus, PillProps['color']> = {
  ACTIVE: 'highlight',
  SUBMITTED: 'pending',
  FAILED: 'severe',
  DEACTIVATED: undefined,
}

const COLUMN_COUNT = 6

const CardFeedRow: React.FC<CardFeedRowProps> = ({
  feed,
  translate,
  onSeeEnrolledCards,
}) => (
  <tr data-testid="card-feeds-row">
    <td css={style.cardProgramCell}>{feed.cardProgramName}</td>
    <td>
      <span css={style.networkCell}>
        {NETWORK_LOGO[feed.cardNetwork] && (
          <img
            src={NETWORK_LOGO[feed.cardNetwork]}
            alt=""
            css={style.networkLogo}
            data-testid="card-feeds-network-logo"
          />
        )}
        {NETWORK_LABEL[feed.cardNetwork] ?? feed.cardNetwork}
      </span>
    </td>
    <td>{feed.cardCount ?? '—'}</td>
    <td>
      <Pill color={STATUS_PILL_COLOR[feed.status]}>
        {STATUS_LABEL[feed.status]
          ? translate(STATUS_LABEL[feed.status])
          : feed.status}
      </Pill>
    </td>
    <td data-testid="card-feeds-reference-id">{feed.referenceId}</td>
    <td css={style.actionCell}>
      <Button
        variant="text"
        onClick={(): void => onSeeEnrolledCards(feed.id)}
        data-testid="card-feeds-see-enrolled"
      >
        {translate('See Cards')}
        <SvgChevronRight viewBox="0 0 24 24" css={style.seeEnrolledChevron} />
      </Button>
    </td>
  </tr>
)

const CardFeeds: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const user = useSelector(selectUserListModelFromState)
  const {
    data: cardFeeds,
    isLoading: isFeedsLoading,
    isError: isFeedsError,
    isFetchedAfterMount: isFeedsFetchedAfterMount,
  } = useQueryCardFeeds(user?.organizationId, isAllowed)

  // Defensive: sidebar gates the menu entry, but a direct URL must redirect
  // home when either flag is off. Wait for the feature query to settle so we
  // don't bounce away during the initial paint.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedsTab)
    analytics.track('card feeds', { action: 'viewed' })
  }, [analytics, isAllowed])

  useEffect(() => {
    if (!isAllowed || !isFeedsError) return
    analytics.track('card feeds', { action: 'load failed' })
  }, [analytics, isAllowed, isFeedsError])

  // With no feeds, route the user straight into creation — but only once we've
  // confirmed *this mount* that the org is empty. isFetchedAfterMount gates out
  // stale cached data from a previous visit, which otherwise bounced users who
  // do have feeds straight to the create page.
  const hasNoFeeds =
    isAllowed &&
    isFeedsFetchedAfterMount &&
    !isFeedsError &&
    cardFeeds?.length === 0
  useEffect(() => {
    if (hasNoFeeds) {
      history.push(createCardFeedUrl())
    }
  }, [hasNoFeeds, history])

  // Hold the gate with a full-page loader while the feature query resolves —
  // otherwise we flash a blank screen before either the page or the redirect.
  if (isFeaturesLoading) {
    return <LoadingBackgroundPage />
  }

  if (!isAllowed) {
    return null
  }

  const handleHelpGuideClick = (): void => {
    Userpilot.trigger('resource_center:jNfDAOvnub')
    analytics.track('card feeds', { action: 'help guide clicked' })
  }

  const handleAddCardFeedClick = (): void => {
    analytics.track('card feeds', { action: 'add card feed clicked' })
    history.push(createCardFeedUrl())
  }

  const handleSeeEnrolledCardsClick = (feedId: string): void => {
    analytics.track('card feeds', { action: 'see enrolled cards clicked' })
    history.push(cardFeedDetailsUrl(feedId))
  }

  const actions = (
    <div css={style.actions}>
      <Button
        variant="secondary"
        onClick={handleHelpGuideClick}
        data-testid="card-feeds-help-guide"
        iconLeft={
          <SvgExternalLink
            viewBox="0 0 24 24"
            css={style.helpGuideIcon}
            data-testid="card-feeds-help-guide-icon"
          />
        }
      >
        {translate('Help Guide')}
      </Button>
      <Button
        variant="primary"
        onClick={handleAddCardFeedClick}
        data-testid="card-feeds-add"
      >
        {`+ ${translate('Add Card Feed')}`}
      </Button>
    </div>
  )

  const feeds = cardFeeds ?? []

  return (
    <>
      <Helmet>
        <title>{translate('Card Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Card Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to bring existing cards into Extend, then issue virtual cards and manage spend in one place.',
            )}
          </span>
        }
        actions={actions}
      >
        <Box noPadding outlineBorder css={style.tableCard}>
          <div css={style.toolbar}>
            <Button
              variant="toolbar"
              dropShadow={false}
              onClick={(): void => undefined}
              data-testid="card-feeds-filter"
              aria-label={translate('Toggle filters')}
            >
              <SvgFilterFunnel />
            </Button>
          </div>
          <table css={style.table} data-testid="card-feeds-table">
            <thead>
              <tr>
                <th scope="col">{translate('Card Program')}</th>
                <th scope="col">{translate('Card Network')}</th>
                <th scope="col">{translate('Number of Cards')}</th>
                <th scope="col">{translate('Status')}</th>
                <th scope="col">{translate('Reference ID')}</th>
                <th scope="col">
                  <span css={style.visuallyHidden}>{translate('Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody
              data-testid={isFeedsLoading ? 'card-feeds-loading' : undefined}
            >
              {isFeedsLoading && <LoadingRows />}
              {!isFeedsLoading && isFeedsError && (
                <tr>
                  <td css={style.messageCell} colSpan={COLUMN_COUNT}>
                    <span data-testid="card-feeds-error">
                      {translate(
                        "We couldn't load your card feeds. Please try again.",
                      )}
                    </span>
                  </td>
                </tr>
              )}
              {!isFeedsLoading &&
                !isFeedsError &&
                feeds.map((feed) => (
                  <CardFeedRow
                    key={feed.id}
                    feed={feed}
                    translate={translate}
                    onSeeEnrolledCards={handleSeeEnrolledCardsClick}
                  />
                ))}
            </tbody>
          </table>
        </Box>
      </LayoutPage>
    </>
  )
}

interface CardFeedRowProps {
  feed: CardFeedResponse
  translate: (str: string) => string
  onSeeEnrolledCards: (feedId: string) => void
}

const LoadingRows: React.FC = () => (
  <>
    {[0, 1, 2].map((row) => (
      <tr key={row} css={style.loadingRow}>
        {Array.from({ length: COLUMN_COUNT }).map((_, cell) => (
          <td key={cell}>
            <span css={style.loadingBar} />
          </td>
        ))}
      </tr>
    ))}
  </>
)

export default CardFeeds
